// The investigating agent on the Email engine record page (Session BCR6, Phase 4): "How the agent wrote this" (the live draft's run
// log) and "What the agent would have written (shadow)" (the background run beside the classic draft; never delivered).
//
// Plain English, office theme tokens only, no new colours. Lists, not tables, so nothing runs past the container (the TABLE RULE);
// no coloured left stripe anywhere. A sentence the checker did not back is HIGHLIGHTED (the same amber mark the failed checks use).
import type { ReactNode } from "react";
import type { EmailAgentRun, EmailAgentSentence, EmailAgentShadow, EmailAgentVerdict } from "../../lib/api";
import { formatDateTime } from "../../lib/format";

const sectionCls = "mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const AMBER_TEXT = "text-[var(--ele-amber-text)]";
const RED_TEXT = "text-ele-danger";

export const TOOL_LABEL: Record<string, string> = {
  read_conversation: "Read the conversation",
  who_is_sender: "Checked who the sender is",
  get_account: "Looked up the account",
  get_schedule: "Looked at the diary",
  get_faults_and_work: "Looked at faults and work",
  get_money: "Looked at invoices and payments",
  get_loler: "Looked at LOLER examination dates",
  get_correspondence: "Looked at earlier correspondence",
  get_rates_and_coverage: "Looked up rates and cover",
  search_past_replies: "Searched the office's past replies",
  search_knowledge_base: "Searched the knowledge base",
};

const KIND_LABEL: Record<EmailAgentSentence["kind"], string> = {
  fact: "States a fact",
  courtesy: "Courtesy",
  question: "Question",
  next_step: "Next step",
};

const VERDICT_LABEL: Record<EmailAgentVerdict, string> = {
  supported: "Checker: backed by the record it cites",
  unsupported: "Checker: NOT backed by the record it cites",
  not_a_fact: "Checker: not a statement of fact",
  states_fact_uncited: "Checker: states a fact but cites no record",
};

const DECISION_LABEL: Record<string, string> = {
  reply: "Would reply",
  needs_input: "Would ask you for a decision",
  no_reply: "Would send no reply",
  refused: "The engine would not draft a reply",
  error: "The agent could not run",
};

const COUNTERPARTY_LABEL: Record<string, string> = {
  customer: "a customer",
  prospect: "a possible new customer",
  supplier: "a supplier",
  subcontractor: "a subcontractor",
  examiner: "an examining body",
  platform: "a property platform or system",
  other: "someone else",
};

const FAILURE_VERDICTS = new Set<string>(["unsupported", "states_fact_uncited"]);

/** The money for a run, in dollars: the API's own price list is in dollars. "Not reported" when the model had no published price. Pure. */
export function costText(costUsd: number | null | undefined): string {
  if (typeof costUsd !== "number" || !Number.isFinite(costUsd)) return "Cost not reported";
  return `$${costUsd < 0.1 ? costUsd.toFixed(4) : costUsd.toFixed(2)}`;
}

export function secondsText(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "Time not reported";
  return `${(ms / 1000).toFixed(1)} seconds`;
}

/** What a lookup was asked, in a short phrase: its inputs as "name: value". Pure. */
export function askedText(input: Record<string, unknown>): string {
  const parts = Object.entries(input ?? {})
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${typeof v === "string" ? v : JSON.stringify(v)}`);
  const text = parts.join("; ");
  return text.length > 200 ? `${text.slice(0, 197)}...` : text;
}

function Heading({ children, note }: { children: ReactNode; note?: ReactNode }) {
  return (
    <>
      <h2 className="font-heading text-lg text-ele-strong">{children}</h2>
      {note && <p className="mt-1 text-xs text-ele-body">{note}</p>}
    </>
  );
}

function Sentence({ s, citedFacts }: { s: EmailAgentSentence; citedFacts: EmailAgentRun["citedFacts"] }) {
  const failed = s.verdict != null && FAILURE_VERDICTS.has(s.verdict);
  return (
    <li className="rounded-ele border border-ele-hairline p-3 text-sm" data-testid="agent-sentence" data-failed={failed ? "true" : "false"}>
      <p className={labelCls}>
        Sentence {s.n} · {KIND_LABEL[s.effectiveKind]}
        {s.effectiveKind !== s.kind ? ` (written as: ${KIND_LABEL[s.kind].toLowerCase()})` : ""}
        {s.kept ? "" : " · not in the final text"}
      </p>
      <blockquote className="border-y border-ele-hairline py-1">
        {failed ? <mark className="rounded bg-ele-amber-wash px-1 text-ele-wash-ink">{s.text}</mark> : <span className="text-ele-strong">{s.text}</span>}
      </blockquote>
      {s.cites.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs text-ele-body">
          {s.cites.map((id) => {
            const f = citedFacts.find((c) => c.id === id);
            return (
              <li key={id} data-testid="agent-citation">
                <span className="text-ele-blue">{id}</span>: {f ? f.text : "the record it cites is not kept on this log"}
                {f ? <span> ({f.source})</span> : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-ele-body">Cites no record.</p>
      )}
      {s.verdict && (
        <p className={`mt-2 text-xs ${failed ? `font-semibold ${AMBER_TEXT}` : "text-ele-body"}`} data-testid="agent-verdict">
          {VERDICT_LABEL[s.verdict]}
          {s.why ? `: ${s.why}` : ""}
        </p>
      )}
    </li>
  );
}

/** The body of a run: situation, lookups, sentences with citations and verdicts, the two layers' failures, models, cost and time. */
export function AgentRunDetail({ run, hideDecision = false }: { run: EmailAgentRun; hideDecision?: boolean }) {
  const failingSentences = run.sentences.filter((s) => s.verdict != null && FAILURE_VERDICTS.has(s.verdict)).length;
  return (
    <div data-testid="agent-run-detail">
      {!hideDecision && (
        <p className="mt-3 text-sm text-ele-strong" data-testid="agent-decision">
          {DECISION_LABEL[run.decision] ?? run.decision}
          {run.reason ? `: ${run.reason}` : ""}
        </p>
      )}
      {run.decisionNeeded && (
        <p className="mt-1 text-sm text-ele-body" data-testid="agent-decision-needed">
          The decision it needs: {run.decisionNeeded}
          {run.options.length > 0 ? ` (${run.options.join(" / ")})` : ""}
        </p>
      )}
      {run.failure && (
        <p className={`mt-2 text-sm ${RED_TEXT}`} data-testid="agent-failure">
          The run ended early: {run.failure}
        </p>
      )}

      {run.situation && (
        <div className="mt-4" data-testid="agent-situation">
          <p className={labelCls}>How it read the situation</p>
          <ul className="space-y-1 text-sm text-ele-body">
            <li>Written by {COUNTERPARTY_LABEL[run.situation.counterparty] ?? run.situation.counterparty}.</li>
            {run.situation.asked.map((a, i) => (
              <li key={`a${i}`}>They ask: {a}</li>
            ))}
            {run.situation.alreadySettled.map((a, i) => (
              <li key={`s${i}`}>Already settled: {a}</li>
            ))}
            <li>{run.situation.replyOwed ? "A reply is owed." : `No reply is owed${run.situation.noReplyReason ? `: ${run.situation.noReplyReason}` : "."}`}</li>
          </ul>
        </div>
      )}

      {run.toolCalls.length > 0 && (
        <div className="mt-4" data-testid="agent-tool-calls">
          <p className={labelCls}>What it looked up</p>
          <ul className="space-y-1 text-sm">
            {run.toolCalls.map((t, i) => {
              const asked = askedText(t.input);
              return (
                <li key={`${t.name}-${i}`} className="text-ele-body" data-testid="agent-tool-call">
                  <span className="text-ele-strong">{TOOL_LABEL[t.name] ?? t.name}</span>
                  {t.byCode ? " (done automatically)" : ""}
                  {asked ? `: ${asked}` : ""}. Returned {t.factIds.length === 1 ? "1 record" : `${t.factIds.length} records`}.
                  {t.note ? <span> {t.note}</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {run.sentences.length > 0 && (
        <div className="mt-4" data-testid="agent-sentences">
          <p className={labelCls}>
            The reply, sentence by sentence
            {failingSentences > 0 ? ` · ${failingSentences === 1 ? "1 sentence" : `${failingSentences} sentences`} the checker did not back` : ""}
          </p>
          <ul className="space-y-3">
            {run.sentences.map((s) => (
              <Sentence key={s.n} s={s} citedFacts={run.citedFacts} />
            ))}
          </ul>
        </div>
      )}

      {run.citationFailures.length > 0 && (
        <div className="mt-4" data-testid="agent-citation-failures">
          <p className={`text-sm font-semibold ${AMBER_TEXT}`}>Citation problems</p>
          <ul className="mt-1 space-y-1 text-sm text-ele-body">
            {run.citationFailures.map((c, i) => (
              <li key={i}>
                Sentence {c.n}: {c.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {run.validatorFailures.length > 0 && (
        <div className="mt-4" data-testid="agent-validator-failures">
          <p className={`text-sm font-semibold ${RED_TEXT}`}>
            The engine's own checks failed {run.validatorFailures.length === 1 ? "1 line" : `${run.validatorFailures.length} lines`}
          </p>
          <ul className="mt-2 space-y-2">
            {run.validatorFailures.map((f, i) => (
              <li key={i} className="text-sm">
                <blockquote className="border-y border-ele-hairline py-1">
                  <mark className="rounded bg-ele-amber-wash px-1 text-ele-wash-ink">{f.line}</mark>
                </blockquote>
                <p className="mt-1 text-ele-body">{f.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {run.disagreements.length > 0 && (
        <div className="mt-4" data-testid="agent-disagreements">
          <p className={`text-sm font-semibold ${AMBER_TEXT}`}>
            Where the checker and the engine's own checks disagreed ({run.disagreements.length})
          </p>
          <ul className="mt-2 space-y-2">
            {run.disagreements.map((d, i) => (
              <li key={i} className="text-sm">
                <blockquote className="border-y border-ele-hairline py-1 text-ele-strong">{d.sentence}</blockquote>
                <p className="mt-1 text-ele-body">
                  {VERDICT_LABEL[d.verifier]}; the engine's own checks {d.validator === "passed" ? "passed it" : "failed it"}. {d.detail}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-xs text-ele-body" data-testid="agent-run-meta">
        Written with {run.models.draft}, checked with {run.models.verifier} · {costText(run.usage.costUsd)} · {secondsText(run.latencyMs)}
        {run.rewrites > 0 ? ` · rewritten ${run.rewrites === 1 ? "once" : `${run.rewrites} times`}` : ""}
      </p>
    </div>
  );
}

/** "How the agent wrote this": the run log behind a live draft the agent wrote. */
export function AgentRunSection({ run }: { run: EmailAgentRun }) {
  return (
    <section className={sectionCls} data-testid="agent-run-section">
      <Heading note="The investigating agent wrote this draft. Every sentence that states a fact points at the record it rests on, and a separate checker read each one.">
        How the agent wrote this
      </Heading>
      <AgentRunDetail run={run} />
    </section>
  );
}

/** "What the agent would have written (shadow)": the background run beside the classic draft. Nothing in it was delivered. */
export function AgentShadowSection({ shadow }: { shadow: EmailAgentShadow }) {
  return (
    <section className={sectionCls} data-testid="agent-shadow-section">
      <Heading note="The agent ran in the background on this message, seeing only what the engine could see when it arrived. This is what it would have written. It was not put in Gmail, not sent and not shown to anyone else.">
        What the agent would have written (shadow)
      </Heading>
      <p className="mt-3 text-sm text-ele-strong" data-testid="shadow-decision">
        {DECISION_LABEL[shadow.decision] ?? shadow.decision}
        {shadow.reason ? `: ${shadow.reason}` : ""}
      </p>
      <p className="mt-1 text-xs text-ele-body">
        Ran {formatDateTime(shadow.at)} · read as of {formatDateTime(shadow.asOf)}
        {shadow.gapCount > 0 ? ` · ${shadow.gapCount === 1 ? "1 record" : `${shadow.gapCount} records`} could not be read as they stood then and were read as they are now` : ""}
        {shadow.run == null ? ` · ${costText(shadow.costUsd)} · ${secondsText(shadow.latencyMs)}` : ""}
      </p>
      {shadow.body ? (
        <div className="mt-3">
          <p className={labelCls}>The text it would have drafted</p>
          <pre data-testid="shadow-body" className="whitespace-pre-wrap break-words rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-sm text-ele-strong">
            {shadow.body}
          </pre>
        </div>
      ) : (
        <p className="mt-2 text-sm text-ele-body">It would have written no text.</p>
      )}
      {shadow.run && <AgentRunDetail run={shadow.run} hideDecision />}
    </section>
  );
}
